import { Avatar, Flex, Typography, theme } from 'antd';
import { themeRawValues } from '@/utils/styleUtils';
import {
  Cell,
  Legend,
  LegendProps,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
} from 'recharts';
import { Payload } from 'recharts/types/component/DefaultLegendContent';
import { GenderDataByPlatformEcho } from './GenderDataByPlatformEcho';
import { GenderPlaysResponse, SelectedPlatform } from '@/types/api/gender';
import { PercentageByPlatformTooltipContentEcho } from '../PercentageByPlatformTooltipContentEcho';
import { mixpanelTrack, RevenueDashboardWidget, uppercaseFirstLetter } from '@/utils';
import { MIXPANEL_EVENT } from '@/utils/mixpanelUtils/events';
import { formatPercentage } from '@/utils/numberFormattingUtils';
import { ChartTooltipWrapperEcho } from '@/components/charts/ChartTooltipEcho';

const chartColors = themeRawValues.colors.chartColors;

const colors = [chartColors.color3, chartColors.color5, chartColors.color6];

export const GenderChartEcho = ({
  data,
  selectedPlatform,
  handlePlatformChange,
}: {
  data: GenderPlaysResponse;
  selectedPlatform: SelectedPlatform<typeof data>;
  handlePlatformChange: (platform: SelectedPlatform<typeof data>) => void;
}) => {
  const { token } = theme.useToken();

  const handleTooltipHover = (event: { name?: string; percent?: number }) => {
    mixpanelTrack(MIXPANEL_EVENT.audience.demographics.chartTooltipHover, {
      widget: RevenueDashboardWidget.GenderDemographics,
      page: 'audience',
      platform: selectedPlatform,
      demographics: 'gender',
      segment: event?.name || '',
      percent: event.percent ? Math.round(event.percent * 100).toString() + '%' : '',
    });
  };

  const filteredData =
    data.plays.find((play) => play.platform === selectedPlatform)?.gender_distribution ?? [];

  const CustomLegend = (props: LegendProps) => {
    const { payload } = props;

    return (
      <Flex
        component="ul"
        align="center"
        justify="center"
        wrap
        gap={token.marginLG}
        style={{ margin: 0, padding: 0, listStyle: 'none' }}
      >
        {payload?.map((entry: Payload, index: number) => (
          <Flex component="li" key={`legend-item-${index}`} align="center" gap={token.marginXS}>
            <Avatar size={12} style={{ backgroundColor: entry.color }} />
            <Typography.Text style={{ color: token.colorText }}>
              {uppercaseFirstLetter(entry.value?.toLowerCase())}
            </Typography.Text>
            {entry.payload && (
              <Typography.Text
                style={{
                  color: token.colorTextSecondary,
                  fontSize: token.fontSizeSM,
                  fontWeight: 300,
                }}
              >
                {formatPercentage(entry.payload?.value)}%
              </Typography.Text>
            )}
          </Flex>
        ))}
      </Flex>
    );
  };

  return (
    <>
      <ResponsiveContainer height={400} width={'100%'}>
        <PieChart
          style={{ width: '100%', aspectRatio: 1 }}
          margin={{ top: 0, right: 32, bottom: 32, left: 32 }}
        >
          <Tooltip
            cursor={false}
            isAnimationActive={true}
            offset={32}
            allowEscapeViewBox={{ y: true }}
            animationDuration={1000}
            wrapperStyle={{ zIndex: token.zIndexPopupBase }}
            content={({ active, payload }: TooltipProps<number, string>) => (
              <ChartTooltipWrapperEcho>
                {active && payload?.[0]?.payload && (
                  <PercentageByPlatformTooltipContentEcho
                    label={`${payload[0].payload.label}`}
                    value={Math.round(payload[0].payload.percentage || 0)}
                    selectedPlatform={selectedPlatform}
                  />
                )}
              </ChartTooltipWrapperEcho>
            )}
          />
          <Pie
            animationBegin={100}
            animationDuration={500}
            data={filteredData}
            dataKey="percentage"
            nameKey="label"
            cx="50%"
            cy="50%"
            innerRadius="50%"
            outerRadius="80%"
            onMouseEnter={handleTooltipHover}
          >
            {filteredData.map((_, index) => (
              <Cell key={`cell-${index}`} stroke={colors[index]} fill={colors[index]} />
            ))}
          </Pie>
          <Legend
            verticalAlign="bottom"
            height={36}
            layout="horizontal"
            textAnchor="middle"
            content={<CustomLegend />}
          />
        </PieChart>
      </ResponsiveContainer>
      <Flex justify="center" style={{ marginBlock: token.marginLG }}>
        <GenderDataByPlatformEcho
          data={data}
          selectedPlatform={selectedPlatform}
          handlePlatformChange={handlePlatformChange}
        />
      </Flex>
    </>
  );
};
